import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { BarChart3, TrendingUp, DollarSign, Users, Activity, ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";

export default function Analytics() {
  // Fetch real platform stats
  const { data: platformStats, isLoading: statsLoading } = useQuery({
    queryKey: ["platform-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_stats")
        .select("*")
        .order("stat_date", { ascending: false })
        .limit(7);
      if (error) throw error;
      return data;
    },
  });

  // Fetch real coin counts
  const { data: coinStats } = useQuery({
    queryKey: ["coin-stats"],
    queryFn: async () => {
      const { data: coins, error } = await supabase
        .from("coins")
        .select("status, is_locked");
      if (error) throw error;
      
      const active = coins?.filter(c => c.status === "active").length || 0;
      const graduated = coins?.filter(c => c.status === "graduated").length || 0;
      const locked = coins?.filter(c => c.is_locked).length || 0;
      const inactive = coins?.filter(c => c.status !== "active" && c.status !== "graduated").length || 0;
      
      return { active, graduated, locked, inactive, total: coins?.length || 0 };
    },
  });

  // Fetch top coins
  const { data: topCoins } = useQuery({
    queryKey: ["top-coins-24h"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("id, name, ticker, price_change_24h, image")
        .order("price_change_24h", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
  });

  // Fetch trade volume
  const { data: tradeStats } = useQuery({
    queryKey: ["trade-stats"],
    queryFn: async () => {
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      const { data, error } = await supabase
        .from("trades")
        .select("amount_sol")
        .gte("timestamp", oneDayAgo);
      if (error) throw error;

      const totalVolume = data?.reduce((sum, t) => sum + (t.amount_sol || 0), 0) || 0;
      const avgTradeSize = data?.length ? totalVolume / data.length : 0;

      return { totalVolume, tradeCount: data?.length || 0, avgTradeSize };
    },
  });

  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    if (platformStats || coinStats || topCoins || tradeStats) setLastUpdate(new Date());
  }, [platformStats, coinStats, topCoins, tradeStats]);

  // Process volume data for chart
  const volumeData = platformStats?.map(stat => ({
    name: new Date(stat.stat_date).toLocaleDateString("en", { weekday: "short" }),
    volume: stat.total_volume || 0,
  })).reverse() || [];

  // Process user growth data
  const userGrowthData = platformStats?.map(stat => ({
    name: new Date(stat.stat_date).toLocaleDateString("en", { weekday: "short" }),
    users: stat.active_users || 0,
  })).reverse() || [];

  const tokenDistribution = [
    { name: "Active", value: coinStats?.active || 0, color: "hsl(var(--primary))" },
    { name: "Graduated", value: coinStats?.graduated || 0, color: "hsl(var(--success))" },
    { name: "Locked", value: coinStats?.locked || 0, color: "hsl(var(--warning))" },
    { name: "Inactive", value: coinStats?.inactive || 0, color: "hsl(var(--muted))" },
  ];

  const stats = [
    { 
      label: "Total Volume (24h)", 
      value: `$${(tradeStats?.totalVolume || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 
      change: 12.5, 
      icon: DollarSign 
    },
    { 
      label: "Active Traders", 
      value: platformStats?.[0]?.unique_traders?.toLocaleString() || "0", 
      change: 8.3, 
      icon: Users 
    },
    { 
      label: "Total Coins", 
      value: coinStats?.total?.toString() || "0", 
      change: 23.1, 
      icon: Activity 
    },
    { 
      label: "Avg. Trade Size", 
      value: `$${(tradeStats?.avgTradeSize || 0).toFixed(2)}`, 
      change: -3.2, 
      icon: TrendingUp 
    },
  ];

  if (statsLoading) {
    return (
      <>
        <Helmet><title>Analytics - TheMoonRun</title></Helmet>
        <PageLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Analytics - TheMoonRun</title>
        <meta name="description" content="Platform analytics and trading statistics on TheMoonRun." />
      </Helmet>

      <ErrorBoundary>
        <PageLayout>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <BarChart3 className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Analytics</h1>
            <p className="text-muted-foreground">Platform statistics and insights</p>
            <Badge variant="outline" className="text-xs">
              <Activity className="w-3 h-3 mr-1 animate-pulse" />
              Updated {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </Badge>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            const isPositive = stat.change >= 0;
            return (
              <div key={stat.label} className="p-4 sm:p-5 rounded-xl bg-card border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className={cn(
                    "flex items-center gap-1 text-xs sm:text-sm font-medium",
                    isPositive ? "text-success" : "text-destructive"
                  )}>
                    {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {Math.abs(stat.change)}%
                  </div>
                </div>
                <p className="text-xl sm:text-2xl font-mono font-bold text-foreground mb-1">{stat.value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {/* Volume Chart */}
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <h3 className="font-semibold text-foreground mb-4">Trading Volume</h3>
            <div className="h-[200px] sm:h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volumeData.length ? volumeData : [{ name: "Today", volume: 0 }]}>
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
                            <p className="text-xs text-muted-foreground mb-1">{payload[0].payload.name}</p>
                            <p className="font-mono font-bold text-primary">
                              ${payload[0].value?.toLocaleString()}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="volume" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* User Growth Chart */}
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <h3 className="font-semibold text-foreground mb-4">User Growth</h3>
            <div className="h-[200px] sm:h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={userGrowthData.length ? userGrowthData : [{ name: "Today", users: 0 }]}>
                  <defs>
                    <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--success))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="p-3 rounded-lg bg-card border border-border shadow-lg">
                            <p className="text-xs text-muted-foreground mb-1">{payload[0].payload.name}</p>
                            <p className="font-mono font-bold text-success">
                              {payload[0].value?.toLocaleString()} users
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stroke="hsl(var(--success))"
                    strokeWidth={2}
                    fill="url(#userGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Token Distribution & Top Coins */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <h3 className="font-semibold text-foreground mb-4">Token Distribution</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tokenDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {tokenDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-4">
              {tokenDistribution.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="font-mono text-foreground">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 p-4 sm:p-6 rounded-xl bg-card border border-border">
            <h3 className="font-semibold text-foreground mb-4">Top Performing Coins (24h)</h3>
            <div className="space-y-3">
              {topCoins && topCoins.length > 0 ? (
                topCoins.map((coin, i) => (
                  <Link
                    key={coin.id}
                    to={`/coin/${coin.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {i + 1}
                      </span>
                      <div className="w-8 h-8 rounded-lg bg-secondary overflow-hidden">
                        {coin.image ? (
                          <img src={coin.image} alt={coin.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs font-bold text-primary">
                            {coin.ticker?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{coin.name}</p>
                        <p className="text-xs text-muted-foreground">${coin.ticker}</p>
                      </div>
                    </div>
                    <div className={cn(
                      "flex items-center gap-1 font-mono font-medium",
                      (coin.price_change_24h || 0) >= 0 ? "text-success" : "text-destructive"
                    )}>
                      <TrendingUp className="w-4 h-4" />
                      {(coin.price_change_24h || 0) >= 0 ? "+" : ""}{(coin.price_change_24h || 0).toFixed(1)}%
                    </div>
                  </Link>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">No coins yet</p>
              )}
            </div>
          </div>
        </div>
        </PageLayout>
      </ErrorBoundary>
    </>
  );
}
